/**
 * SnippetViewerPanel - Panel para visualizar fragmentos de PDF
 * Panel separado e independiente del visor PDF
 */

import React from 'react';
import { Scissors, X } from 'lucide-react';
import type { PanelTabType } from './PanelToolSelector';
import { useTheme } from '../contexts/ThemeContext';
import SnippetViewer from './SnippetViewer';
import type { PDFSnippet } from '../services/api';

interface SnippetViewerPanelProps {
  snippetToView: PDFSnippet | null;
  onClose?: () => void;
  onSnippetClick?: (snippet: PDFSnippet) => void;
  panelPosition?: 'left' | 'center' | 'right';
  onPanelChange?: (panel: PanelTabType) => void;
  availablePanels?: PanelTabType[];
  currentPanel?: PanelTabType;
  onNavigateBack?: () => void;
  onNavigateForward?: () => void;
  canGoBack?: boolean;
  canGoForward?: boolean;
  hideHeader?: boolean; // Prop opcional para ocultar el header cuando se usa dentro de DocumentViewer
}

const SnippetViewerPanel: React.FC<SnippetViewerPanelProps> = ({
  snippetToView,
  onClose,
  onSnippetClick,
  panelPosition,
  onPanelChange,
  availablePanels,
  currentPanel,
  onNavigateBack,
  onNavigateForward,
  canGoBack = false,
  canGoForward = false,
  hideHeader = false,
}) => {
  const { theme } = useTheme();

  // Función para generar el header
  const renderHeader = () => (
    <div className={`flex-shrink-0 border-b ${theme.colors.border.primary} pl-3 pr-1 py-2 flex items-center justify-between gap-2 h-12 overflow-hidden`}>
      <div className="flex items-center gap-2 min-w-0">
        <Scissors className={`w-3.5 h-3.5 flex-shrink-0 ${theme.colors.text.primary}`} />
        <span className={`text-xs font-semibold ${theme.colors.text.primary} leading-tight`}>Visor Fragmentos</span>
      </div>
      <div className="flex items-center gap-1">
        {onClose && (
          <button
            type="button"
            onClick={onClose}
            className={`min-w-[32px] min-h-[32px] p-1.5 rounded-lg ${theme.colors.bg.secondary} ${theme.colors.text.secondary} hover:${theme.colors.bg.tertiary} transition-colors flex items-center justify-center border ${theme.colors.border.primary}`}
            title="Cerrar panel"
          >
            <X className="w-4 h-4" />
          </button>
        )}
      </div>
    </div>
  );

  // Si no hay snippet seleccionado, mostrar placeholder
  if (!snippetToView) {
    const placeholderContent = (
      <div className={`h-full w-full flex flex-col ${theme.colors.bg.primary}`}>
        {!hideHeader && renderHeader()}
        <div className="flex-1 flex items-center justify-center p-8">
          <div className="text-center">
            <Scissors className={`w-12 h-12 mx-auto mb-4 ${theme.colors.text.secondary} opacity-50`} />
            <p className={`text-sm ${theme.colors.text.secondary}`}>
              No hay fragmento seleccionado
            </p>
          </div>
        </div>
      </div>
    );

    return placeholderContent;
  }

  // Renderizar el visor de snippet
  const panelContent = (
    <div className={`h-full w-full flex flex-col ${theme.colors.bg.primary}`}>
      {!hideHeader && renderHeader()}
      <div className="flex-1 overflow-hidden">
        <SnippetViewer
          snippet={snippetToView}
          onOpenInPDFViewer={onSnippetClick}
          onCopy={(text) => {
            navigator.clipboard.writeText(text);
          }}
        />
      </div>
    </div>
  );

  return panelContent;
};

export default SnippetViewerPanel;

